import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from "react-dom";
import "./style.css";
import PreviousValue from './PreviousValue';
import { buildAutomationChildTestId, resolveAutomationTestId } from './testId';

const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    const context = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
};

export const AddressAutocompleter = ({
  id,
  onSelect,
  value,
  onChange,
  onCommit,
  error,
  showErr,
  desc,
  placeholder,
  isDisabled = false,
  prevValue,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const latestValueRef = useRef(value);
  const lastAutofillSyncRef = useRef("");
  const lastResolvedQueryRef = useRef("");
  // Monotonic token serializing async place resolutions (suggestion getDetails, typed-address
  // geocodes from blur/autofill-sync). Multiple resolutions can be in flight at once and Google
  // biases raw-text geocodes to the user's region, so a slow stale response landing after a newer
  // one would flip street/country back and forth ("country toggling"). Only the latest request
  // may apply; typing also invalidates anything in flight.
  const resolutionSeqRef = useRef(0);
  // True while an explicit suggestion selection is resolving; typed-address geocodes (blur /
  // autofill-sync) must not start during that window or they would supersede the user's choice.
  const predictionResolutionPendingRef = useRef(false);
  const fieldTestId = resolveAutomationTestId(id, placeholder);

  useEffect(() => {
    latestValueRef.current = value;
    lastAutofillSyncRef.current =
      typeof value === "string" ? value.trim() : String(value ?? "");
  }, [value]);

  // Convert the selected place into an address object.
  const handlePlaceSelect = useCallback((place) => {
    if (!place || !place.address_components) {
      console.error("No address components available in the selected place:", place);
      return;
    }

    const addressComponents = {
      buildingNumber: '',
      street: '',
      pinCode: '',
      city: '',
      state: '',
      country: '',
    };

    // Index components by type BEFORE picking any of them. Google returns address_components in a
    // fixed most-specific-first order, so resolving them inside the loop would let whichever type
    // appears earliest in the array win - e.g. administrative_area_level_2 ("Denver County") is
    // returned before administrative_area_level_1 ("Colorado"), and neighborhood ("North Capitol
    // Hill") before locality ("Denver"). Indexing first lets us apply an explicit precedence.
    const byType = {};
    place.address_components.forEach((component) => {
      (component.types || []).forEach((type) => {
        if (!byType[type]) {
          byType[type] = component;
        }
      });
    });

    const longNameOf = (type) => byType[type]?.long_name || '';
    // Return the first type in the given precedence order that Google actually supplied.
    const pickByPrecedence = (...types) => {
      for (const type of types) {
        const name = longNameOf(type);
        if (name) {
          return name;
        }
      }
      return '';
    };

    const streetNumber = longNameOf('street_number');
    const streetRoute = longNameOf('route');
    const premise = longNameOf('premise');
    const subpremise = longNameOf('subpremise');
    const postalCode = longNameOf('postal_code').replace(/ /g, '');
    const postalCodeSuffix = longNameOf('postal_code_suffix').replace(/ /g, '');

    // City: Google varies this heavily by country (postal_town in the UK, sublocality in IN, ...).
    // administrative_area_level_2 is the county and stays a last resort.
    addressComponents.city = pickByPrecedence(
      'locality',
      'postal_town',
      'sublocality_level_1',
      'sublocality',
      'administrative_area_level_3',
      'neighborhood',
      'administrative_area_level_2',
    );

    // State/Province: administrative_area_level_1 is the state in the US, CA and AU (and the country
    // constituent in the UK). administrative_area_level_2 is the COUNTY (e.g. "Kent County" for
    // Dover, DE) and must never win over it - downstream partners such as Bridge require the real
    // state. It is kept only as a fallback for the rare place with no level_1 at all.
    addressComponents.state = pickByPrecedence(
      'administrative_area_level_1',
      'administrative_area_level_2',
    );

    addressComponents.country = byType['country']?.short_name || '';

    addressComponents.pinCode = postalCodeSuffix ? `${postalCode}-${postalCodeSuffix}` : postalCode;
    addressComponents.buildingNumber = subpremise || premise || "APT# NA";
    addressComponents.street = (
      (streetNumber ? `${streetNumber} ` : "")
      + streetRoute
    ).trim() || place?.name || place?.formatted_address?.split(',')?.[0] || latestValueRef.current || '';

    onSelect(addressComponents);
    const normalizedSelection = `${addressComponents.street || ''}`.trim();
    if (normalizedSelection) {
      lastResolvedQueryRef.current = normalizedSelection;
    }
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
  }, [onSelect]);

  const resolveAddressTextToPlace = useCallback((input, onResolved) => {
    const normalizedInput = typeof input === "string" ? input.trim() : "";
    if (!normalizedInput) {
      return;
    }

    if (!window.google || !window.google.maps) {
      return;
    }

    const resolutionToken = ++resolutionSeqRef.current;
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: normalizedInput }, (results, status) => {
        if (resolutionSeqRef.current !== resolutionToken) {
          return; // A newer resolution (or fresh typing) superseded this one.
        }
        if (status === window.google.maps.GeocoderStatus.OK && Array.isArray(results) && results[0]) {
          onResolved(results[0]);
        }
      });
    } catch (error) {
      console.error("Exception while geocoding typed address:", error);
    }
  }, []);

  const maybeResolveTypedAddress = useCallback((input) => {
    const normalizedInput = typeof input === "string" ? input.trim() : "";
    if (!normalizedInput || normalizedInput.length < 6) {
      return;
    }

    if (predictionResolutionPendingRef.current) {
      return; // An explicit suggestion selection is resolving; don't compete with it.
    }

    if (normalizedInput === lastResolvedQueryRef.current) {
      return;
    }

    resolveAddressTextToPlace(normalizedInput, (place) => {
      lastResolvedQueryRef.current = normalizedInput;
      handlePlaceSelect(place);
    });
  }, [handlePlaceSelect, resolveAddressTextToPlace]);

  const resolvePredictionToPlace = useCallback((prediction, onResolved) => {
    if (!prediction?.place_id) {
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.warn("Google Maps API is not available. Cannot fetch place details.");
      return;
    }

    const resolutionToken = ++resolutionSeqRef.current;
    predictionResolutionPendingRef.current = true;

    const resolveFromGeocoder = () => {
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ placeId: prediction.place_id }, (results, status) => {
          predictionResolutionPendingRef.current = false;
          if (resolutionSeqRef.current !== resolutionToken) {
            return; // Superseded by a newer resolution or fresh typing.
          }
          if (status === window.google.maps.GeocoderStatus.OK && Array.isArray(results) && results[0]) {
            onResolved(results[0]);
          } else {
            console.error("Error geocoding selected place:", status);
          }
        });
      } catch (error) {
        predictionResolutionPendingRef.current = false;
        console.error("Exception while geocoding selected place:", error);
      }
    };

    try {
      const dummyDiv = document.createElement('div');
      const service = new window.google.maps.places.PlacesService(dummyDiv);
      service.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['address_components', 'formatted_address', 'name', 'place_id'],
        },
        (place, status) => {
          if (resolutionSeqRef.current !== resolutionToken) {
            predictionResolutionPendingRef.current = false;
            return; // Superseded by a newer resolution or fresh typing.
          }
          const hasAddressComponents = Array.isArray(place?.address_components) && place.address_components.length > 0;
          if (status === window.google.maps.places.PlacesServiceStatus.OK && hasAddressComponents) {
            predictionResolutionPendingRef.current = false;
            onResolved(place);
            return;
          }

          resolveFromGeocoder();
        }
      );
    } catch (error) {
      console.error("Exception while fetching place details:", error);
      resolveFromGeocoder();
    }
  }, []);


  // Fetch predictions from the Google Places API.
  const fetchPredictions = useCallback((input) => {
    // If Google Maps API isn't loaded, simply exit (normal input behavior).
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.warn("Google Maps API is not available. Falling back to normal input.");
      setSuggestions([]);
      return;
    }

    try {
      const service = new window.google.maps.places.AutocompleteService();
      // No `types` filter on purpose. `types: ['address']` only returns precise street addresses and
      // silently drops named premises/buildings (e.g. "Nahta Niwas, Huda Colony, Sector 46, Gurugram"),
      // which are how many non-US addresses are actually listed. Leaving it unset matches what Google
      // Maps itself suggests; handlePlaceSelect already falls back to place.name / formatted_address
      // when a result carries no street_number/route.
      service.getPlacePredictions({ input }, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
        } else {
          console.error("Error fetching place predictions:", status);
          setSuggestions([]);
        }
      });
    } catch (error) {
      console.error("Exception while fetching predictions:", error);
      setSuggestions([]);
    }
  }, []);

  // Debounced version of fetchPredictions (300ms delay).
  const debouncedFetchPredictions = useMemo(() => debounce(fetchPredictions, 300), [fetchPredictions]);

  // Handle input changes and fetch suggestions if input is long enough.
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    // Fresh typing invalidates any in-flight place resolution so a slow geocode
    // can't overwrite what the user is entering now.
    resolutionSeqRef.current += 1;
    onChange(inputValue);
    if (inputValue && inputValue.length > 2) {
      debouncedFetchPredictions(inputValue);
    } else {
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
  };

  // Close the suggestions dropdown if the user clicks outside.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setSuggestions([]);
        setActiveSuggestionIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncAutofilledValue = () => {
      const node = inputRef.current;
      if (!node || document.activeElement === node) {
        return;
      }

      const domValue = (node.value ?? "").trim();
      const stateValue =
        typeof latestValueRef.current === "string"
          ? latestValueRef.current.trim()
          : String(latestValueRef.current ?? "");

      if (!domValue || domValue === stateValue || domValue === lastAutofillSyncRef.current) {
        return;
      }

      lastAutofillSyncRef.current = domValue;
      onChange(domValue);
      maybeResolveTypedAddress(domValue);
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
    };

    syncAutofilledValue();

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      syncAutofilledValue();
      attempts += 1;
      if (attempts >= 20) {
        window.clearInterval(intervalId);
      }
    }, 250);

    const handleWindowFocus = () => {
      window.setTimeout(syncAutofilledValue, 120);
    };

    window.addEventListener("pageshow", handleWindowFocus);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("pageshow", handleWindowFocus);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [maybeResolveTypedAddress, onChange]);

  const handleInputBlur = () => {
    const normalizedValue = typeof value === "string" ? value.trim() : value;
    if (typeof normalizedValue === "string" && normalizedValue !== value) {
      flushSync(() => {
        onChange(normalizedValue);
      });
    }
    maybeResolveTypedAddress(normalizedValue);
    onCommit?.(normalizedValue);
    setTimeout(() => {
      /* `containerRef.current.contains(document.activeElement)` checks if the newly focused element is still within the same container */
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        setSuggestions([]);
        setActiveSuggestionIndex(-1);
      }
    }, 150);
  };

  // Handle keyboard navigation.
  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((prevIndex) => (prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
        const selectedPrediction = suggestions[activeSuggestionIndex];
        resolvePredictionToPlace(selectedPrediction, handlePlaceSelect);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
  };

  // Handle click on a suggestion item.
  const handleSuggestionClick = (prediction) => {
    resolvePredictionToPlace(prediction, handlePlaceSelect);
  };

  return (
    <div className="inputfield__container mt-0" style={{ position: 'relative' }} ref={containerRef} data-testid={fieldTestId}>
      <div className={`inputfield__innercontainer ${prevValue?.has ? "is-changed" : isDisabled ? "is-disabled" : ""} px-2`}>
        <input
          ref={inputRef}
          id={id}
          data-testid={buildAutomationChildTestId(fieldTestId, "input")}
          disabled={isDisabled}
          type="text"
          value={value ?? ""}
          maxLength={40}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className={`inputfield__input ${error && showErr ? 'is-invalid' : ''} mx-1 ${isDisabled ? "cursor-disabled" : ""}`}
          autoComplete="street-address"
        />
      </div>

      {desc ? <em className="inputfield__desc d-block" data-testid={buildAutomationChildTestId(fieldTestId, "description")}>
        {desc ? desc : ""}
      </em> : null}

      <PreviousValue
        value={prevValue?.value}
        show={prevValue?.has}
        testId={buildAutomationChildTestId(fieldTestId, "previous")}
      />

      {error && showErr && <em className="inputfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>{error}</em>}

      {(suggestions.length > 0 && !isDisabled) ? (
        <div
          className="autocomplete-dropdown"
          style={{
            position: 'absolute',
            top: '46px',
            left: "10px",
            right: 0,
            zIndex: 1000,
            borderRadius: "0px 0px 10px 10px",
            marginRight: "10px",
            background: 'white',
            border: '1px solid #eee',
            borderTop: "0px",
            maxHeight: '200px',
            overflowY: 'auto',
            boxShadow: '0 4px 4px rgba(0, 0, 0, 0.2)',
          }}
          role="listbox"
          data-testid={buildAutomationChildTestId(fieldTestId, "suggestions")}
        >
          {suggestions.map((prediction, index) => (
            <div
              key={prediction.place_id}
              role="option"
              aria-selected={activeSuggestionIndex === index}
              data-testid={buildAutomationChildTestId(fieldTestId, "suggestion")}
              data-record-id={prediction.place_id}
              className={`autocomplete-item ${activeSuggestionIndex === index ? 'active' : ''}`}
              style={{
                padding: '8px',
                cursor: 'pointer',
                borderTop: index === 0 ? 'none' : '1px solid #eee',
                backgroundColor: activeSuggestionIndex === index ? '#eee' : 'white'
              }}
              onMouseDown={(e) => e.preventDefault()} // Prevents the input from blurring when clicking
              onClick={() => handleSuggestionClick(prediction)}
              onMouseEnter={() => setActiveSuggestionIndex(index)}
            >
              {prediction.description}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
